import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { coerceDateTime } from '../my-journeys/myJourneysModel';

const text = (value, fallback = '') => (typeof value === 'string' && value.trim() ? value.trim() : fallback);

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

export const SAVED_CONTENT_FILTERS = {
  ALL: 'ALL',
  SOCIAL_POST: 'SOCIAL_POST',
  JOURNEY_PUBLICATION: 'JOURNEY_PUBLICATION',
};

export function normalizeSavedContentItem(value, t) {
  const contentType = value?.contentType || '';
  const targetId = snowflakeId(value?.targetId);
  const fallbackTitle = contentType === 'SOCIAL_POST'
    ? (t ? t('travel.saved.defaultPostTitle') : 'Saved post')
    : (t ? t('travel.saved.defaultJourneyTitle') : 'Saved journey');
  return {
    contentType,
    targetId,
    title: text(value?.title, fallbackTitle),
    summary: text(value?.summary, ''),
    coverUrl: resolveMemberMediaUrl(value?.coverFileReference),
    creatorId: snowflakeId(value?.creatorId),
    creatorDisplayName: text(value?.creatorDisplayName, ''),
    savedAt: coerceDateTime(value?.savedAt),
  };
}

/**
 * 收藏項目詳情路由。公開行程可帶 intent=copy，導向既有 Public Detail Copy CTA（不在此呼叫 Copy API）。
 */
export function itemRoute(item, options = {}) {
  if (!item?.targetId) return '#';
  if (item.contentType === 'SOCIAL_POST') return `/social/posts/${item.targetId}`;
  if (item.contentType === 'JOURNEY_PUBLICATION') {
    const base = `/public/journeys/${item.targetId}`;
    return options.intent === 'copy' ? `${base}?intent=copy` : base;
  }
  return '#';
}

export function itemKey(item) {
  return `${item?.contentType || 'UNKNOWN'}:${item?.targetId}`;
}

export function normalizeSavedContentPage(payload, t) {
  const items = Array.isArray(payload?.items)
    ? payload.items.map((item) => normalizeSavedContentItem(item, t)).filter((item) => item.targetId != null)
    : [];
  const hasMore = Boolean(payload?.hasMore);
  const nextCursorUpdatedAt = coerceDateTime(payload?.nextCursorUpdatedAt) || payload?.nextCursorUpdatedAt || null;
  const nextCursorSaveId = payload?.nextCursorSaveId != null
    ? snowflakeId(payload.nextCursorSaveId) ?? payload.nextCursorSaveId
    : null;
  const nextCursor = hasMore && nextCursorUpdatedAt && nextCursorSaveId != null
    ? { cursorUpdatedAt: nextCursorUpdatedAt, cursorSaveId: nextCursorSaveId }
    : null;
  return { items, hasMore, nextCursor };
}

export function appendSavedContentItems(current, incoming) {
  const seen = new Set((current || []).map((item) => itemKey(item)));
  const merged = [...(current || [])];
  for (const item of incoming || []) {
    const key = itemKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(item);
  }
  return merged;
}

export function savedContentErrorMessage(cause, t) {
  if (typeof t === 'function') {
    return resolveSafeUserVisibleError(cause, t, 'travel.saved.loadFailedTitle');
  }
  return resolveSafeUserVisibleError(cause, () => '無法載入收藏，請稍後再試。', 'travel.saved.loadFailedTitle');
}

export function contentTypeLabel(contentType, t) {
  if (contentType === 'SOCIAL_POST') return t('travel.saved.typePost');
  if (contentType === 'JOURNEY_PUBLICATION') return t('travel.saved.typeJourney');
  return contentType;
}

export function emptyStateKey(filter) {
  if (filter === SAVED_CONTENT_FILTERS.SOCIAL_POST) return 'posts';
  if (filter === SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION) return 'journeys';
  return 'all';
}
